import { useAxios } from "@/config";
import {
  BookStatusEntry,
  GenerateBookPresignedUrlResponse,
  DatasetTypes,
  RiskDataSet,
  Geocoder,
  Contexts,
} from "@/types";
import { defineStore } from "pinia";
import Papa from "papaparse";
import { ColumnDefinition, DatasetDefinition } from "@gamma/types.data";
import { useOrgSettings, useMap, useAuth } from "@/services";

interface BookState {
  content: any;
  file: File | undefined;
  isLoadingUpload: boolean;
  dataSetDefinition: DatasetDefinition | undefined;
  books: BookStatusEntry[];
  risks: RiskDataSet[];
  geocoderData: Geocoder;
  userOrganisation: string;
  isGeocodingEnabled: boolean;
}

export const useBooks = defineStore("books", {
  state: (): BookState => ({
    content: [],
    file: undefined,
    isLoadingUpload: false,
    dataSetDefinition: undefined,
    books: [],
    risks: [],
    geocoderData: { country: undefined, provider: undefined },
    userOrganisation: "",
    isGeocodingEnabled: false,
  }),
  getters: {
    isFileInvalid(state: BookState): boolean {
      return !(
        state.file?.type === "text/csv" ||
        (state.file?.type === "application/vnd.ms-excel" &&
          state.file?.name.endsWith(".csv"))
      );
    },
    hasUniqueId(state: BookState): boolean {
      return !!state.dataSetDefinition?.columns.some(
        (x) => x.context === Contexts.UniqueId
      );
    },
    hasAddress(state: BookState): boolean {
      const addressContexts = new Set(["AddressLine", "Address", "Postcode"]);
      return !!state.dataSetDefinition?.columns.some((x) =>
        addressContexts.has(x.context)
      );
    },
    isUserOrganisation(): string {
      return useAuth().user?.["custom:organisation"];
    },
    isMetaDataValid(): boolean {
      return this.hasUniqueId && this.hasAddress;
    },
    hasAddressId(state: BookState) {
      return !!state.dataSetDefinition?.columns.some(
        (x) => x.context === Contexts.AddressId
      );
    },
    isDataTaggingValid(): boolean {
      if (!this.isGeocodingEnabled) {
        this.geocoderData.country = undefined;
        this.geocoderData.provider = undefined;
      }
      return (
        (this.hasAddressId && !this.isGeocodingEnabled) ||
        (this.isGeocodingEnabled &&
          this.geocoderData.country !== undefined &&
          this.geocoderData.provider !== undefined)
      );
    },
    selectedRisks(state: BookState): RiskDataSet[] {
      return state.risks.filter((x) => x.isSelected);
    },
    baseUrl(): string {
      return (
        useOrgSettings().settings?.applicationSettings?.baseDataApiUrl ||
        process.env.VUE_APP_API_BASE_DATA_URL ||
        "./data-api"
      );
    },
  },
  actions: {
    setFile(file: any) {
      this.file = file;
      if (this.file) {
        this.parseFile(this.file);
      }
    },
    setContent(content: any) {
      this.content = content;
    },
    setGeocodingEnabled(isGeocodingEnabled: boolean) {
      this.isGeocodingEnabled = isGeocodingEnabled;
    },
    clearFile() {
      this.content = [];
      this.file = undefined;
      this.dataSetDefinition = undefined;
      this.isGeocodingEnabled = false;
    },
    updateDatasetDefinitionColumns(columns: ColumnDefinition[]) {
      if (this.dataSetDefinition) {
        this.dataSetDefinition.columns = columns;
      }
    },
    async reloadBookStatus(
      userId: string,
      bookId: string
    ): Promise<BookStatusEntry | undefined> {
      const { $http } = useAxios();
      const result = await $http.get<BookStatusEntry>(
        `${this.baseUrl}/book/${userId}/${bookId}/latest`
      );

      const book = this.books.find((x) => x.id === bookId);
      if (!book || !result.data) return;

      Object.assign(book, result.data);

      return book;
    },
    setRisks(risksData: RiskDataSet[]) {
      this.risks = [...risksData];
    },
    async loadBooks() {
      const { $http } = useAxios();
      const result = await $http.get<BookStatusEntry[]>(`${this.baseUrl}/book`);
      this.books = result.data;
    },
    async deleteBook(book: BookStatusEntry) {
      const mapStore = useMap();
      const { $http } = useAxios();
      await $http.post<BookStatusEntry[]>(
        `${this.baseUrl}/book/delete/${book.userId}/${book.id}`
      );
      mapStore.unloadBook(book);
      await this.loadBooks();
    },
    async saveDataSet(
      organisation: string,
      taggedDatasets: Array<string>,
      dataset: DatasetDefinition,
      geocodingdataset?: Geocoder
    ): Promise<GenerateBookPresignedUrlResponse> {
      const { $http } = useAxios();
      const result = await $http.put<GenerateBookPresignedUrlResponse>(
        `${this.baseUrl}/book/save`,
        { organisation, dataset, taggedDatasets, geocodingdataset }
      );
      return result.data;
    },
    async upload() {
      if (!this.dataSetDefinition || !this.file) {
        return;
      }
      const response = await this.saveDataSet(
        this.isUserOrganisation,
        this.selectedRisks.map((x) => x.id),
        this.dataSetDefinition,
        this.isGeocodingEnabled ? this.geocoderData : undefined
      );
      const { $http } = useAxios();
      await $http.put(response.signedUrl, this.file);
      await this.loadBooks();
    },
    createDataSet(items: any[][], file: any, dataHeaders: string[]) {
      this.content = items;
      const orgBusinessLine = useOrgSettings().selectedBusinessLine;
      if (!orgBusinessLine) return;
      const columns: ColumnDefinition[] = dataHeaders.map((field: string) => {
        const samples = items.map((item: any) => item[field]);
        return {
          columnName: field,
          type: getColumnType(samples),
          visible: true,
          context: "",
          displayName: field,
          columnsNameInDoubleQuotes: "",
          measurement: undefined,
          sortable: true,
          filterable: true,
          updatable: false,
        };
      });

      // set dataSetDefinition
      this.dataSetDefinition = {
        dataSetType: DatasetTypes.Policies,
        businessLine: orgBusinessLine.name,
        name: file.name,
        includeInAccumulation: false,
        columns: columns,
      };
    },
    async parseFile(file: File) {
      const items: any[] = [];
      await Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,

        step: (results, parser) => {
          items.push(results.data);
          if (items.length === 10) {
            this.createDataSet(items, file, results.meta.fields ?? []);
            parser.abort();
          }
        },
      });
    },
  },
});

function getColumnType(samplesItems: any[]): string {
  const types = samplesItems.filter((x) => !!x).map((x) => getItemType(x));
  if (!types.length) return "String";
  const typeSets = new Set(types);
  return typeSets.size > 1 ? "String" : types[0];
}

function getItemType(item: any): string {
  switch (typeof item) {
    case "number":
      return getNumberActualType(item);
    default:
      return "String";
  }
}

function getNumberActualType(num: number): any {
  return num.toString().indexOf(".") === -1 ? "Integer" : "Decimal";
}
